import { useCallback, useMemo } from 'react';
import { useNodeConnections, useReactFlow, useStore } from '@xyflow/react';
import { selectNodeSelectedById } from '@/components/nodes/store-selectors';

export type IncomingImageInput = {
  idx: number;
  /** 上游节点 ID（指向真实节点，用于 focusNodeById） */
  sourceId: string;
  /**
   * 这张图在排序/排版/state 中的稳定 ID。
   * - 普通上游：`up:${sourceId}`
   * - multi 主体上游首图：`up:${sourceId}`（与普通上游格式一致，向后兼容旧 imageGridOrder/referenceImageOrder）
   * - multi 主体上游其余原图：`up:${sourceId}:1`, `up:${sourceId}:2`, ...
   * - 主体聚合项（仅 mention 列表使用，不出现在缩略图网格里）：`up:${sourceId}:group`
   */
  rid: string;
  /** multi 主体内子图索引（0=首图）；非 multi 主体为 undefined */
  subIndex?: number;
  /** multi 主体节点贡献的总图片数；非 multi 主体为 undefined */
  subTotal?: number;
  /**
   * 类型：image（普通图项 / multi 主体单张子图）| subjectGroup（聚合项，仅 mention 用）
   * 默认 image。
   */
  kind?: 'image' | 'subjectGroup';
  /**
   * 仅 subjectGroup 项使用：子图在所属数组中的下标列表，
   * 在 mention 选中时按该顺序一次性插入所有子图 mention。
   */
  groupChildIdxs?: number[];
  url?: string;
  label: string;
  role?: 'start' | 'end' | 'reference';
  mediaType?: 'image' | 'video' | 'audio';
};

type UseIncomingImageInputsParams = {
  id: string;
  t: (key: string, vars?: any) => string;
  includeFrameRole?: boolean;
};

/**
 * 读取“指向当前节点的入边”中所有图像来源（image/upload(image)）并按连线顺序产出缩略图列表。
 * - 为了避免全局计算，仅在当前节点 selected 时才返回非空列表
 * - 可选读取 frameRole（用于 image -> video 的首/尾/参考帧标注）
 */
export function useIncomingImageInputs(params: UseIncomingImageInputsParams) {
  const { id, t, includeFrameRole = false } = params;
  const { getNode, getEdges, setNodes, setCenter, getViewport } = useReactFlow();
  const incomingConnections = useNodeConnections({
    id,
    handleType: 'target',
  });

  const selectIsTargetSelected = useMemo(
    () => selectNodeSelectedById(id),
    [id]
  );
  const isTargetSelected = useStore(selectIsTargetSelected);

  const incomingImageInputs = useMemo<IncomingImageInput[]>(() => {
    try {
      // Only compute when this node is selected (avoid work for all nodes)
      if (!isTargetSelected) return [] as IncomingImageInput[];
      if (!incomingConnections.length) return [] as IncomingImageInput[];

      const edgeById = new Map(
        (getEdges?.() || []).map((edge: any) => [String(edge?.id), edge])
      );
      const targetNode = getNode(id) as any;
      const mirror = (includeFrameRole
        ? (((targetNode as any)?.data?.edgeFrameRoles || {}) as Record<
            string,
            IncomingImageInput['role']
          >)
        : {}) as Record<string, IncomingImageInput['role'] | undefined>;

      const resolveAssetPreview = (
        d: any,
        url: string | undefined
      ): string | undefined => {
        if (url && url.startsWith('Asset://') && d?.assetPreviewUrl) {
          return d.assetPreviewUrl;
        }
        return url;
      };

      const getSrcPreviewUrl = (src: any): string | undefined => {
        try {
          if (!src) return undefined;
          if (src.type === 'upload') {
            const fileType = String(
              (src.data as any)?.fileType || ''
            ).toLowerCase();
            const url = (src.data as any)?.content?.url as string | undefined;
            if (fileType === 'image' && url) {
              return resolveAssetPreview(src.data, url);
            }
            const contentType = (src.data as any)?.content?.type;
            if (url && contentType && String(contentType).startsWith('image/')) {
              return resolveAssetPreview(src.data, url);
            }
            return undefined;
          }
          if (src.type === 'image') {
            const d = (src.data as any) || {};
            const generatedUrl = d?.generated?.url as string | undefined;
            const generatedUrls = d?.generated?.urls as string[] | undefined;
            const contentUrl = d?.content?.url as string | undefined;
            return (
              resolveAssetPreview(d, generatedUrl) ||
              (Array.isArray(generatedUrls) ? generatedUrls[0] : undefined) ||
              resolveAssetPreview(d, contentUrl)
            );
          }
          return undefined;
        } catch {
          return undefined;
        }
      };

      const items: IncomingImageInput[] = [];
      let contentIdx = 0;

      for (let i = 0; i < incomingConnections.length; i += 1) {
        const connection = incomingConnections[i] as any;
        const sourceId = String(connection?.source || '');
        if (!sourceId) continue;
        const src = getNode(sourceId) as any;
        if (!src) continue;
        const isImageNode =
          src.type === 'image' ||
          (src.type === 'upload' &&
            (String((src.data as any)?.fileType || '') === 'image' ||
              String((src.data as any)?.content?.type || '').startsWith(
                'image/'
              )));
        if (!isImageNode) continue;

        const baseLabel =
          String((src?.data as any)?.assetNote || '').trim() ||
          String((src?.data as any)?.name || '').trim() ||
          (t('Image Node') || 'Image Node');

        const edge = connection?.edgeId
          ? edgeById.get(String(connection.edgeId))
          : undefined;
        const role = includeFrameRole
          ? (((edge?.data as any)?.frameRole as IncomingImageInput['role']) ??
              mirror[sourceId])
          : undefined;

        // multi 主体上传节点：subjectImages 长度 > 1 时展开为多个缩略图条目
        // 每个子图独立排序、独立展示；rid 第 0 张沿用 `up:${sourceId}` 保持向后兼容
        const subjectImages =
          src.type === 'upload' && Array.isArray((src.data as any)?.subjectImages)
            ? ((src.data as any).subjectImages as unknown[])
                .map((u) => (typeof u === 'string' ? u.trim() : ''))
                .filter((u) => u.length > 0)
            : [];

        if (subjectImages.length > 1) {
          for (let k = 0; k < subjectImages.length; k += 1) {
            const subUrl = resolveAssetPreview(src.data, subjectImages[k]) || subjectImages[k];
            // 失效子图（无 URL）直接跳过，避免 mention idx 重复为 0、生成时序号错位
            if (!subUrl) continue;
            contentIdx++;
            items.push({
              idx: contentIdx,
              sourceId,
              rid: k === 0 ? `up:${sourceId}` : `up:${sourceId}:${k}`,
              subIndex: k,
              subTotal: subjectImages.length,
              url: subUrl,
              label: `${baseLabel} ${k + 1}/${subjectImages.length}`,
              role,
            });
          }
          continue;
        }

        const previewUrl = getSrcPreviewUrl(src);
        if (previewUrl) contentIdx++;

        items.push({
          idx: previewUrl ? contentIdx : 0,
          sourceId,
          rid: `up:${sourceId}`,
          url: previewUrl,
          label: baseLabel,
          role,
        });
      }

      return items;
    } catch {
      return [] as IncomingImageInput[];
    }
  }, [
    getEdges,
    getNode,
    id,
    includeFrameRole,
    incomingConnections,
    isTargetSelected,
    t,
  ]);

  const focusNodeById = useCallback(
    (sourceId: string) => {
      try {
        const nodeRef = getNode(sourceId) as any;
        try {
          setNodes((prev: any[]) =>
            prev.map((n: any) => ({
              ...n,
              selected: String(n.id) === String(sourceId),
            }))
          );
        } catch {}
        if (!nodeRef) return;
        const x = Number(nodeRef?.position?.x ?? 0);
        const y = Number(nodeRef?.position?.y ?? 0);
        const w = Number(nodeRef?.measured?.width ?? 300);
        // Keep current zoom to avoid "jump zooming"
        const vp = (() => {
          try {
            return getViewport();
          } catch {
            return { zoom: undefined as any };
          }
        })();
        setCenter(x + w / 2, y, { duration: 350, zoom: vp?.zoom });
      } catch {}
    },
    [getNode, setNodes, setCenter, getViewport]
  );

  return { incomingImageInputs, focusNodeById, isTargetSelected } as const;
}